import { OrderHistorySortColumns } from './../signing-history/signing-history.component';
import { DatePipe } from '@angular/common';
import { ICertificateData } from './../../manage/manage';
import { Component, OnInit, OnDestroy, ViewChild, forwardRef, AfterViewInit, Input } from '@angular/core';
import { InfoService } from '@app/core/info/info.service';
import { Subject } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
//import { throwMatDialogContentAlreadyAttachedError } from '@angular/material/table';
import {
  IOrder, HistoryOrder, OrderStatus,
  OrderStatusEnum,
  OrderHistoryRequest,
} from '@app/certificates/history/history';
import { Store } from '@ngrx/store';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { ActionPacksShowOrder, ActionPacksMakeOrderSuccess, ActionPacksRestoreOrderPayment } from '@app/certificates/packs/packs.reducer';
import { IPackItem } from '@app/certificates/packs/packs';
import { TranslateService } from '@ngx-translate/core';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { PackService } from '@app/certificates/packs/pack.service';
import { IPaymentStatusResponse } from '@app/shared/interfaces/response-interfaces';
import { environment } from '@env/environment';
import { HistoryService } from '../history.service';
import { takeUntil } from 'rxjs/operators';
import {
  ISpringResponseBody,
  IPaymentDataPostResponse
} from '@app/shared/interfaces/response-interfaces';

@Component({
  selector: 'cloud-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss']
})
export class OrderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;

  unsubscribe$: Subject<void> = new Subject<void>();
  orderHistoryDataSource: MatTableDataSource<HistoryOrder> = new MatTableDataSource<HistoryOrder>();
  @Input() displayOptions: OrderHistoryRequest = {};
  plans: IPackItem[];

  readonly icons = iconCodes;
  isLoading = true;
  documentNumber: any;
  isPaginatorVisible = true;
  displayAllOption = false;
  minEndDateValue: string;
  maxStartDateValue: string;
  resetCertificate: string;
  resetPlan: string;
  fromDate: string;
  toDate: string;
  orderStatusLabels = [
    'ALL',
    'PAID',
    'CANCELED',
    'ORDERED',
    'NEW'
  ];

  readonly displayedColumns = [
    'status',
    'orderCode',
    'orderDate',
    'paymentDate',
    'planName',
    'planSigningCount',
    'price',
    'actions'
  ];

  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;

  constructor(
    public info: InfoService,
    private store: Store<any>,
    private packService: PackService,
    private translateService: TranslateService,
    private historyService: HistoryService,
    private tableHelper: MatTableHelper
  ) {
  }

  ngOnInit() {
    this.info.dismissAll();
    this.store.select(s => s.packs.items).subscribe(data => this.plans = data);
    this.displayOptions.limit = 10;
    this.displayOptions.offset = 0;
    this.loadOrderHistory();
  }

  /**
   * Set the paginator and sort after the view init since this component will
   * be able to query its view for the initialized paginator and sort.
   */
  ngAfterViewInit() {
    this.orderHistoryDataSource.paginator = this.paginator;
    this.orderHistoryDataSource.sort = this.sort;
    this.orderHistoryDataSource.sortingDataAccessor = this.tableHelper.sortingDataAccessorForOrders;
    this.orderHistoryDataSource.filterPredicate = this.tableHelper.filterPredicateForOrders;
  }

  setDataSource(orders: IOrder[]) {
    const historyOrder: HistoryOrder[] = orders.map(order => new HistoryOrder(order));
    this.orderHistoryDataSource = new MatTableDataSource(historyOrder);
  }

  onPayment(historyOrder: HistoryOrder): void {

    let orderData: IOrder = {
      orderCode: historyOrder.orderCode,
      orderId: historyOrder.orderId,
      orderDate: historyOrder.orderDate,
      paymentDate: historyOrder.paymentDate,
      orderStatus: null,
      planId: historyOrder.planId,
      planName: historyOrder.planName,
      price: historyOrder.price,
      currencyCode: historyOrder.currencyCode,
      planSigningCount: historyOrder.planSigningCount,
      planType: historyOrder.planType,
      axOrderNumber: historyOrder.axOrderNumber,
      axInvoiceNumber: historyOrder.axInvoiceNumber
    }

    this.store.dispatch(new ActionPacksRestoreOrderPayment(orderData));
  }

  onDownloadInvoice(historyOrder: HistoryOrder, copyOriginal: string) {
    let cd = '';
    this.packService.getInvoice(historyOrder.axInvoiceNumber, copyOriginal)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            const file = new Blob([result.body], { type: 'application/pdf' });
            cd = result.headers.get('Content-Disposition');
            let fileName = cd.substring(cd.indexOf('=') + 2, (cd.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(file, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(file);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    this.orderHistoryDataSource.filter = filterValue;
  }

  hasData(): boolean {
    return (this.orderHistoryDataSource && this.orderHistoryDataSource.data.length !== 0);
  }

  notPaid(status: OrderStatus): boolean {
    return status.key === OrderStatusEnum.NEW;
  }
  hasInvoice(historyOrder: HistoryOrder) {
    return historyOrder.status.key === OrderStatusEnum.PAID && historyOrder.axInvoiceNumber !== null;
  }

  containAXOrderNumber(historyOrder: HistoryOrder) {
    return historyOrder.axOrderNumber !== null;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get currentLang() {
    return this.translateService.currentLang;
  }

  loadOrderHistory() {
    //Instead of modifying the displayOptions object by adding new field, we use a temporary variable to
    //hold the axOrderNumber value. This is required as the ngModel will change the visible value in the UI
    //field when the getOrderCodeByAxOrderNum() is executed.
    let tmpAxNumHolder = this.displayOptions.orderCode;
    this.displayOptions.orderCode = this.getOrderCodeByAxOrderNum(this.displayOptions.orderCode);

    this.historyService.getOrdersHistory(this.displayOptions).subscribe(res => {
      if (res.data) {
        this.setDataSource(res.data.content);
      }
      this.documentNumber = res.data.size;
      this.setPaginator();
      this.isLoading = false;
    });
    this.displayOptions.orderCode = tmpAxNumHolder;
  }

  getOrderCodeByAxOrderNum(axOrderNumber: string): string {
    let match = '';
    this.orderHistoryDataSource.data.forEach(order => {
      if (order.axOrderNumber === axOrderNumber) {
        match = order.orderCode;
      }
    });

    return match;
  }

  setSelectedPlan(plan) {
    if (this.plans.length > 1) {
      this.displayAllOption = true;
    }
    this.displayOptions.offset = 0;
    this.displayOptions.planId = plan;
    this.loadOrderHistory();
  }

  displayAllPlans() {
    delete this.displayOptions.planId;
    this.loadOrderHistory();
  }

  setOrderStatus() {
    if (this.displayOptions.status && this.displayOptions.status === OrderStatusEnum.ALL) {
      delete this.displayOptions.status;
    }
    this.displayOptions.offset = 0;
    this.loadOrderHistory();
  }

  sortByHeader(sorter) {
    const columnName = OrderComponent.getColumnName(sorter.id);
    if (!sorter._arrowDirection) {
      sorter._arrowDirection = 'desc';
    }
    this.displayOptions.orderByFilter = columnName;
    this.displayOptions.order = sorter._arrowDirection.toUpperCase();
    this.loadOrderHistory();
  }

  static getColumnName(name) {
    switch (name) {
      case 'status':
        return OrderHistorySortColumns.ORDER_STATUS;
      case 'orderCode':
        return OrderHistorySortColumns.ORDER_CODE;
      case 'orderDate':
        return OrderHistorySortColumns.ORDER_DATE;
      case 'planName':
        return OrderHistorySortColumns.PLAN_NAME;
      case 'certSerialNumber':
        return OrderHistorySortColumns.CERTIFICATE_SERIAL_NUMBER;
      case 'paymentDate':
        return OrderHistorySortColumns.PAYMENT_DATE;
      case 'price':
        return OrderHistorySortColumns.PRICE;
      case 'planSigningCount':
        return OrderHistorySortColumns.PLAN_SIGNING_COUNT;
      default:
        return OrderHistorySortColumns.ORDER_STATUS;
    }
  }

  getOrderStatusText(value) {
    switch (value) {
      case 'ALL':
        return this.translateService.instant('historyTexts.all');
      case 'PAID':
        return this.translateService.instant('historyTexts.orderStatusPaid');
      case 'CANCELED':
        return this.translateService.instant('historyTexts.orderStatusCanceled');
      case 'ORDERED':
        return this.translateService.instant('historyTexts.orderStatusOrdered');
      case 'NEW':
        return this.translateService.instant('historyTexts.orderStatusNew');
      default:
        return this.translateService.instant('historyTexts.orderStatusPaid');
    }
  }

  getOrderStatusNumber(value) {
    switch (value) {
      case 'ALL':
        return OrderStatusEnum.ALL;
      case 'PAID':
        return OrderStatusEnum.PAID;
      case 'CANCELED':
        return OrderStatusEnum.CANCELED;
      case 'ORDERED':
        return OrderStatusEnum.IN_PROGRESS;
      case 'NEW':
        return OrderStatusEnum.NEW;
    }
  }

  onPaginateChange(paginator) {
    this.displayOptions.limit = paginator.pageSize;
    this.displayOptions.offset += (paginator.pageIndex - paginator.previousPageIndex);
    this.loadOrderHistory();
  }

  getPlanName(plan: IPackItem) {
    return plan.name[this.translateService.currentLang];
  }

  setRequestDates() {
    const pip: DatePipe = new DatePipe('bg-BG');

    if (this.fromDate) {
      this.minEndDateValue = this.fromDate;
      const startDate = new Date(this.fromDate).setHours(0, 0, 0, 0);
      this.displayOptions.fromOrderDate = encodeURIComponent(pip.transform(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }

    if (this.toDate) {
      this.maxStartDateValue = this.toDate;
      const endDate = new Date(this.toDate).setHours(23, 59, 59, 999);
      this.displayOptions.toOrderDate = encodeURIComponent(pip.transform(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ'));
    }
    this.displayOptions.offset = 0;
    this.loadOrderHistory();
  }

  resetFilters() {
    this.fromDate = '';
    this.maxStartDateValue = '';
    this.toDate = '';
    this.minEndDateValue = '';

    this.displayAllOption = false;
    this.displayOptions = {};
    this.displayOptions.offset = 0;
    this.displayOptions.limit = 10;
    this.resetCertificate = null;
    this.resetPlan = null;
    this.loadOrderHistory();
  }

  isOverLimit() {
    return (this.orderHistoryDataSource.data.length >= 500);
  }

  searchOrder() {
    if (this.displayOptions.orderCode) {
      this.displayOptions.offset = 0;
      this.loadOrderHistory();
    }
  }

  clearText() {
    this.displayOptions.orderCode = '';
    delete this.displayOptions.orderCode;
    this.displayOptions.offset = 0;
    this.loadOrderHistory();
  }

  private setPaginator() {
    this.paginator.pageIndex = this.displayOptions.offset;
    this.paginator.pageSize = this.displayOptions.limit;
    this.paginator.length = this.documentNumber;
    this.isPaginatorVisible = this.documentNumber > this.paginator.pageSize;
  }
  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }
  getPayOrderNumber(historyOrder: HistoryOrder) {
    return historyOrder.axOrderNumber !== null ? historyOrder.axOrderNumber : historyOrder.orderCode;
  }
  isBeforeCutoff(timestamp: number): boolean {
    const orderDate = new Date(timestamp);
    const cutoffDate = new Date('2026-01-01');
    return orderDate < cutoffDate;
  }

  formatOrderDate(timestamp: number): string {
    const date = new Date(timestamp);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  }

}
